import {
  Box,
  Text,
  Button,
  Stack,
  Group,
  Badge,
  Loader,
  Center,
  TextInput,
  AspectRatio,
  Modal,
  Collapse,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import {
  Listing,
  IPopulatedListing,
  getStatusColor,
  getConfirmationColor,
} from '../types/Listing.model';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteListing, getListingsByAppId, updateListingConfirmation } from '../Api';
import { useStore } from '../store/Store';
import { notifications } from '@mantine/notifications';
import {
  IconExternalLink,
  IconSearch,
  IconCalendar,
  IconClock,
  IconTrash,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { clientRoutes, websiteDomain } from '../ApiPaths';
import { ProductListPreviewPicture } from './ProductListPreviewPicture';
import { ArticleNumberBadge } from './badges';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';

const DESTRUCTIVE_COLOR = '#ef4444';

const accentInputStyles = (accentColor: string) => ({
  input: {
    '&:focus': {
      borderColor: accentColor,
      boxShadow: `0 0 0 1px ${accentColor}`,
    },
  },
});
type AppListingManagerProps = {
  appId: string;
};

// Helper function to get sort priority for listings
const getListingSortPriority = (listing: IPopulatedListing): number => {
  // First priority: Pending confirmations
  if (
    listing.appConfirmation === Listing.Confirmation.PENDING ||
    listing.productConfirmation === Listing.Confirmation.PENDING
  ) {
    return 0;
  }
  // Second priority: Declined/Unlisted
  if (
    listing.status === Listing.Status.UNLISTED ||
    listing.appConfirmation === Listing.Confirmation.DECLINED ||
    listing.productConfirmation === Listing.Confirmation.DECLINED
  ) {
    return 1;
  }
  // Last priority: Fully confirmed and listed
  return 2;
};

export function AppListingManager({ appId }: AppListingManagerProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const userProfile = useStore((state) => state.userProfile);
  const accentColor = userProfile.qrCodeColor || DEFAULT_QR_CODE_COLOR;
  const [isLoading, setIsLoading] = useState(true);
  const [listingsOpen, setListingsOpen] = useState(false);
  const [listings, setListings] = useState<IPopulatedListing[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [listingPendingDeleteId, setListingPendingDeleteId] = useState<string | null>(null);
  const [isDeletingListing, setIsDeletingListing] = useState(false);
  const userId = userProfile._id;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const appListings = await getListingsByAppId(appId);
        // Sort the listings
        const sortedListings = [...appListings].sort((a, b) => {
          const priorityA = getListingSortPriority(a);
          const priorityB = getListingSortPriority(b);
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          // If same priority, sort by date (newest first)
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });
        setListings(sortedListings);
      } catch (error) {
        console.error('Error fetching data:', error);
        setListings([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (appId) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [appId]);

  const handleUpdateConfirmation = async (listingId: string, isConfirmed: boolean) => {
    setIsLoading(true);
    try {
      if (!userId) {
        notifications.show({
          title: t('editProduct.editError'),
          message: t('editProduct.userNotLoggedIn'),
          color: 'red',
        });
        return;
      }

      const result = await updateListingConfirmation(listingId, userId, true, isConfirmed);

      // If both sides declined, the listing is deleted (result is null)
      if (result === null) {
        // Remove the listing from the local state
        setListings((prevListings) => prevListings.filter((l) => l._id !== listingId));
        notifications.show({
          title: t('editProduct.editSuccess'),
          message: t('editProduct.listingDeleted'),
          color: 'green',
        });
        setIsLoading(false);
        return;
      }

      await refreshListings();

      notifications.show({
        title: t('editProduct.editSuccess'),
        message: isConfirmed
          ? t('editProduct.listingConfirmedSuccess')
          : t('editProduct.listingDeactivatedSuccess'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating listing confirmation:', error);
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.listingUpdateError'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshListings = async () => {
    const appListings = await getListingsByAppId(appId);
    const sortedListings = [...appListings].sort((a, b) => {
      const priorityA = getListingSortPriority(a);
      const priorityB = getListingSortPriority(b);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    setListings(sortedListings);
  };

  const confirmDeleteListing = async () => {
    if (!listingPendingDeleteId) return;

    if (!userId) {
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.userNotLoggedIn'),
        color: 'red',
      });
      return;
    }

    const listingId = listingPendingDeleteId;
    setIsDeletingListing(true);
    try {
      await deleteListing(listingId);
      setListings((prevListings) => prevListings.filter((l) => l._id !== listingId));
      setListingPendingDeleteId(null);
      notifications.show({
        title: t('editProduct.editSuccess'),
        message: t('editProduct.listingDeleted'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting listing:', error);
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.listingDeleteError'),
        color: 'red',
      });
    } finally {
      setIsDeletingListing(false);
    }
  };

  const searchQuery = searchTerm.trim().toLowerCase();
  const filteredListings = listings.filter(
    (listing) =>
      listing.product.productName.toLowerCase().includes(searchQuery) ||
      (listing.product.articleNumber ?? '').toLowerCase().includes(searchQuery)
  );

  const listingsTitle = (
    <Group gap={6} wrap="nowrap" align="center">
      <Text fw={600} size="sm">
        {t('editProduct.productListings')}
      </Text>
      {listings.length > 0 ? (
        <Text size="xs" c="dimmed" fw={600}>
          {listings.length}
        </Text>
      ) : null}
    </Group>
  );

  if (isLoading) {
    return (
      <Box>
        <Text fw={600} size="sm" mb="xs">
          {t('editProduct.productListings')}
        </Text>
        <Center>
          <Loader size="sm" color="gray" />
        </Center>
      </Box>
    );
  }

  const canCollapse = listings.length > 0;

  return (
    <Box>
      {canCollapse ? (
        <UnstyledButton
          onClick={() => setListingsOpen((open) => !open)}
          aria-expanded={listingsOpen}
          aria-label={t(
            listingsOpen ? 'editProduct.hideProductListings' : 'editProduct.showProductListings'
          )}
          style={{ color: 'inherit' }}
        >
          <Group gap={6} wrap="nowrap" align="center">
            {listingsOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            {listingsTitle}
          </Group>
        </UnstyledButton>
      ) : (
        listingsTitle
      )}

      {listings.length > 0 ? (
        <Collapse in={listingsOpen}>
          <Box mb="md" mt="sm">
            <TextInput
              placeholder={t('editProduct.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} color={theme.colors.gray[6]} />}
              size="sm"
              style={{ width: '100%' }}
              styles={accentInputStyles(accentColor)}
            />
          </Box>

          {filteredListings.length > 0 ? (
            <Stack gap="sm">
              {filteredListings.map((listing) => (
                <Box
                  key={listing._id}
                  p="md"
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                  }}
                >
                  <Group justify="space-between" align="flex-start">
                    <Group gap="md" align="flex-start">
                      <AspectRatio ratio={1} w={60} h={60} style={{ flexShrink: 0 }}>
                        <Box
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#f8f9fa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {listing.product._id &&
                          listing.product.productImage ? (
                            <ProductListPreviewPicture
                              entityId={listing.product._id}
                              productImage={listing.product.productImage}
                              imageVersion={listing.product.imageVersion}
                              alt={
                                listing.product.productName || 'Product image'
                              }
                              fit="contain"
                            />
                          ) : (
                            <Text size="xs" c="dimmed" ta="center">
                              No Image
                            </Text>
                          )}
                        </Box>
                      </AspectRatio>
                      <div>
                        <Group gap="xs" mb="xs">
                          <Text>{listing.product.productName}</Text>
                        {listing.product.articleNumber && (
                          <ArticleNumberBadge
                            articleNumber={listing.product.articleNumber}
                            size="sm"
                          />
                        )}
                        <a
                          href={`${websiteDomain}${clientRoutes.product}`.replace(
                            ':id',
                            listing.product._id
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          <IconExternalLink size={16} style={{ verticalAlign: 'middle' }} />
                        </a>
                      </Group>
                      <Group gap="xs">
                        <Badge color={getStatusColor(listing.status)} variant="light" size="sm">
                          {listing.status}
                        </Badge>
                        <Badge
                          color={getConfirmationColor(listing.appConfirmation)}
                          variant="light"
                          size="sm"
                        >
                          App: {listing.appConfirmation}
                        </Badge>
                        <Badge
                          color={getConfirmationColor(listing.productConfirmation)}
                          variant="light"
                          size="sm"
                        >
                          Product: {listing.productConfirmation}
                        </Badge>
                      </Group>
                      <Group gap="md" mt={4}>
                        <Group gap={4}>
                          <IconCalendar size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                          <Text size="xs" c="dimmed">
                            {new Date(listing.createdAt).toLocaleDateString()}
                          </Text>
                        </Group>
                        <Group gap={4}>
                          <IconClock size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                          <Text size="xs" c="dimmed">
                            {new Date(listing.updatedAt).toLocaleDateString()}
                          </Text>
                        </Group>
                      </Group>
                      </div>
                    </Group>
                    <Stack gap={6} align="flex-end">
                      {listing.appConfirmation === Listing.Confirmation.CONFIRMED ? (
                        <Button
                          variant="subtle"
                          size="xs"
                          color="gray"
                          style={{ color: DESTRUCTIVE_COLOR, fontWeight: 500 }}
                          onClick={() => handleUpdateConfirmation(listing._id, false)}
                        >
                          {t('editProduct.deactivate')}
                        </Button>
                      ) : (
                        <Button
                          variant="subtle"
                          size="xs"
                          style={{
                            backgroundColor: accentColor,
                            color: '#fff',
                            fontWeight: 500,
                          }}
                          styles={{
                            root: { '&:hover': { filter: 'brightness(0.93)' } },
                          }}
                          onClick={() => handleUpdateConfirmation(listing._id, true)}
                        >
                          {t('editProduct.confirm')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="xs"
                        styles={{
                          root: {
                            borderColor: DESTRUCTIVE_COLOR,
                            color: DESTRUCTIVE_COLOR,
                            fontWeight: 500,
                            '&:hover': { backgroundColor: `${DESTRUCTIVE_COLOR}14` },
                          },
                        }}
                        onClick={() => setListingPendingDeleteId(listing._id)}
                      >
                        {t('editProduct.deleteListing')}
                      </Button>
                    </Stack>
                  </Group>
                </Box>
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center">
              {t('editProduct.noMatchingProducts')}
            </Text>
          )}
        </Collapse>
      ) : (
        <Text size="sm" c="dimmed" ta="center">
          {t('editProduct.noProductListings')}
        </Text>
      )}

      <Modal
        opened={listingPendingDeleteId !== null}
        onClose={() => !isDeletingListing && setListingPendingDeleteId(null)}
        centered
        size="sm"
        radius="md"
        padding="md"
        overlayProps={{ blur: 2 }}
        closeOnClickOutside={!isDeletingListing}
        closeOnEscape={!isDeletingListing}
        title={
          <Group gap="xs" wrap="nowrap">
            <IconTrash size={18} style={{ color: DESTRUCTIVE_COLOR, flexShrink: 0 }} />
            <Text fw={600} size="md" lh={1.3}>
              {t('editProduct.listingDeleteModalTitle')}
            </Text>
          </Group>
        }
        styles={{
          header: {
            padding: '12px 16px 0',
            marginBottom: 0,
            minHeight: 'unset',
          },
          title: {
            fontWeight: 600,
            margin: 0,
          },
          body: {
            padding: '12px 16px 16px',
          },
          content: {
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed" lh={1.5}>
            {t('editProduct.listingDeleteConfirm')}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              size="sm"
              disabled={isDeletingListing}
              onClick={() => setListingPendingDeleteId(null)}
            >
              {t('editProduct.cancel')}
            </Button>
            <Button
              size="sm"
              loading={isDeletingListing}
              loaderProps={{ color: 'white' }}
              style={{
                backgroundColor: DESTRUCTIVE_COLOR,
                color: '#fff',
                fontWeight: 500,
              }}
              styles={{
                root: { '&:hover': { filter: 'brightness(0.93)' } },
              }}
              onClick={confirmDeleteListing}
            >
              {t('editProduct.deleteListing')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
